import { useCallback, useEffect, useMemo, useState } from "react";

import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import { Novedad, NovedadesService } from "@services/NovedadesService";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";

export interface StatusMappingEditableGroup extends NovedadesExportServiceModel.StatusMappingGroup {
    query: string;
    debouncedQuery: string;
}

interface UseExportNovedadesStatusMappingConfigurationViewModelProps {
    orgID: string;
    exportService: NovedadesExportService;
    novedadesService: NovedadesService;
}

const normalizeText = (value: string): string => {
    return value.toLowerCase().replace(/\s/g, "");
};

const normalizeGroupForCompare = (group: StatusMappingEditableGroup) => ({
    totalsField: group.totalsField,
    label: group.label,
    builtIn: group.builtIn === true,
    dayFlags: group.dayFlags,
    novedadAcronyms: [...group.novedadAcronyms].sort(),
});

const totalAssignedAcronymCount = (groups: StatusMappingEditableGroup[]): number => {
    return groups.reduce((total, group) => total + group.novedadAcronyms.length, 0);
};

const customGroupCount = (groups: StatusMappingEditableGroup[]): number => {
    return groups.filter((group) => group.builtIn !== true).length;
};

const changedGroupCount = (
    groups: StatusMappingEditableGroup[],
    savedGroups: StatusMappingEditableGroup[],
): number => {
    const savedByField = new Map(savedGroups.map((group) => [
        group.totalsField,
        JSON.stringify(normalizeGroupForCompare(group)),
    ]));
    const currentByField = new Map(groups.map((group) => [
        group.totalsField,
        JSON.stringify(normalizeGroupForCompare(group)),
    ]));
    const fields = new Set([
        ...Array.from(savedByField.keys()),
        ...Array.from(currentByField.keys()),
    ]);
    return Array.from(fields).filter((field) => savedByField.get(field) !== currentByField.get(field)).length;
};

const generateTotalsField = (
    label: string,
    existingFields: Set<string>,
): string => {
    const normalizedLabel = label
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    const baseField = `custom_${normalizedLabel || "column"}`;
    let nextField = baseField;
    let suffix = 2;

    while (existingFields.has(nextField)) {
        nextField = `${baseField}_${suffix}`;
        suffix += 1;
    }

    return nextField;
};

const toEditableGroups = (
    groups: NovedadesExportServiceModel.StatusMappingGroup[] = [],
): StatusMappingEditableGroup[] => {
    return groups.map((group) => ({
        ...group,
        novedadAcronyms: group.novedadAcronyms ?? [],
        query: "",
        debouncedQuery: "",
    }));
};

export const useExportNovedadesStatusMappingConfigurationViewModel = ({
    orgID,
    exportService,
    novedadesService,
}: UseExportNovedadesStatusMappingConfigurationViewModelProps) => {
    const [groups, setGroups] = useState<StatusMappingEditableGroup[]>([]);
    const [savedGroups, setSavedGroups] = useState<StatusMappingEditableGroup[]>([]);
    const [novedades, setNovedades] = useState<Novedad[]>([]);
    const [newColumnLabel, setNewColumnLabel] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const load = useCallback(async () => {
        if (!orgID) {
            setGroups([]);
            setSavedGroups([]);
            setNovedades([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(undefined);

        try {
            const [configuration, novedadesCatalog] = await Promise.all([
                exportService.getStatusMappingConfiguration(orgID),
                novedadesService.getNovedades(orgID),
            ]);
            const editableGroups = toEditableGroups(configuration.groups);
            setGroups(editableGroups);
            setSavedGroups(editableGroups);
            setNovedades(novedadesCatalog);
        } catch (loadError) {
            console.error(loadError);
            setError("No se pudo cargar la configuración de columnas.".localized());
        } finally {
            setLoading(false);
        }
    }, [exportService, novedadesService, orgID]);

    useEffect(() => {
        void load();
    }, [load]);

    const novedadesByAcronym = useMemo(() => {
        return novedades.reduce((result, novedad) => {
            result[novedad.acronym] = novedad;
            return result;
        }, {} as Record<string, Novedad>);
    }, [novedades]);

    const selectedAcronyms = useMemo(() => {
        return new Set(groups.flatMap((group) => group.novedadAcronyms));
    }, [groups]);

    const hasChanges = useMemo(() => {
        return JSON.stringify(groups.map(normalizeGroupForCompare)) !==
            JSON.stringify(savedGroups.map(normalizeGroupForCompare));
    }, [groups, savedGroups]);

    const canCreateColumn = newColumnLabel.trim().length > 0 && !saving;

    const statusMappingAnalyticsPayload = useCallback((
        nextGroups: StatusMappingEditableGroup[] = groups,
    ) => ({
        totalGroupCount: nextGroups.length,
        customGroupCount: customGroupCount(nextGroups),
        totalAssignedAcronymCount: totalAssignedAcronymCount(nextGroups),
        changedGroupCount: changedGroupCount(nextGroups, savedGroups),
    }), [groups, savedGroups]);

    const selectedNovedadesForGroup = useCallback((group: StatusMappingEditableGroup): Novedad[] => {
        return group.novedadAcronyms.map((acronym) => {
            return novedadesByAcronym[acronym] ?? {
                acronym,
                name: acronym,
                disabled: true,
            };
        });
    }, [novedadesByAcronym]);

    const resultsForGroup = useCallback((group: StatusMappingEditableGroup): Novedad[] => {
        const normalizedQuery = normalizeText(group.debouncedQuery);
        if (!normalizedQuery) {
            return [];
        }

        return novedades
            .filter((novedad) => !novedad.disabled)
            .filter((novedad) => !selectedAcronyms.has(novedad.acronym))
            .filter((novedad) => {
                return normalizeText([
                    novedad.acronym,
                    novedad.name,
                    novedad.payrollCode,
                ].filter(Boolean).join(" ")).includes(normalizedQuery);
            })
            .slice(0, 8);
    }, [novedades, selectedAcronyms]);

    const updateGroup = useCallback((
        totalsField: string,
        updater: (group: StatusMappingEditableGroup) => StatusMappingEditableGroup,
    ) => {
        setGroups((currentGroups) => currentGroups.map((group) => {
            if (group.totalsField !== totalsField) {
                return group;
            }
            return updater(group);
        }));
    }, []);

    const setGroupQuery = useCallback((totalsField: string, query: string) => {
        updateGroup(totalsField, (group) => ({ ...group, query }));
    }, [updateGroup]);

    const setGroupDebouncedQuery = useCallback((totalsField: string, debouncedQuery: string) => {
        updateGroup(totalsField, (group) => ({ ...group, debouncedQuery }));
    }, [updateGroup]);

    const addNovedadToGroup = useCallback((totalsField: string, novedad: Novedad) => {
        let trackedGroup: StatusMappingEditableGroup | undefined;
        const nextGroups = groups.map((group) => {
            if (group.totalsField !== totalsField || group.novedadAcronyms.includes(novedad.acronym)) {
                return group;
            }

            trackedGroup = {
                ...group,
                novedadAcronyms: [...group.novedadAcronyms, novedad.acronym],
                query: "",
                debouncedQuery: "",
            };
            return trackedGroup;
        });

        if (!trackedGroup) {
            return;
        }

        setGroups(nextGroups);
        NovedadesExportAnalyticsHelper.trackStatusMappingNovedadAdded({
            totalsField,
            builtIn: trackedGroup.builtIn === true,
            novedadAcronym: novedad.acronym,
            groupAcronymCount: trackedGroup.novedadAcronyms.length,
            totalAssignedAcronymCount: totalAssignedAcronymCount(nextGroups),
        });
    }, [groups]);

    const removeNovedadFromGroup = useCallback((totalsField: string, novedad: Novedad) => {
        let trackedGroup: StatusMappingEditableGroup | undefined;
        const nextGroups = groups.map((group) => {
            if (group.totalsField !== totalsField || !group.novedadAcronyms.includes(novedad.acronym)) {
                return group;
            }

            trackedGroup = {
                ...group,
                novedadAcronyms: group.novedadAcronyms.filter((acronym) => acronym !== novedad.acronym),
            };
            return trackedGroup;
        });

        if (!trackedGroup) {
            return;
        }

        setGroups(nextGroups);
        NovedadesExportAnalyticsHelper.trackStatusMappingNovedadRemoved({
            totalsField,
            builtIn: trackedGroup.builtIn === true,
            novedadAcronym: novedad.acronym,
            groupAcronymCount: trackedGroup.novedadAcronyms.length,
            totalAssignedAcronymCount: totalAssignedAcronymCount(nextGroups),
        });
    }, [groups]);

    const createColumn = useCallback(() => {
        if (!canCreateColumn) {
            return;
        }

        const label = newColumnLabel.trim();
        const existingFields = new Set(groups.map((group) => group.totalsField));
        const totalsField = generateTotalsField(label, existingFields);
        const nextGroups = [
            ...groups,
            {
                totalsField,
                label,
                builtIn: false,
                novedadAcronyms: [],
                query: "",
                debouncedQuery: "",
            },
        ];
        setGroups(nextGroups);
        NovedadesExportAnalyticsHelper.trackStatusMappingColumnCreated({
            totalsField,
            customGroupCount: customGroupCount(nextGroups),
            totalGroupCount: nextGroups.length,
        });
        setNewColumnLabel("");
    }, [canCreateColumn, groups, newColumnLabel]);

    const removeGroup = useCallback((totalsField: string) => {
        const removedGroup = groups.find((group) => {
            return group.builtIn !== true && group.totalsField === totalsField;
        });
        const nextGroups = groups.filter((group) => {
            return group.builtIn === true || group.totalsField !== totalsField;
        });

        if (!removedGroup) {
            return;
        }

        setGroups(nextGroups);
        NovedadesExportAnalyticsHelper.trackStatusMappingColumnRemoved({
            totalsField,
            customGroupCount: customGroupCount(nextGroups),
            totalGroupCount: nextGroups.length,
        });
    }, [groups]);

    const cancel = useCallback(() => {
        NovedadesExportAnalyticsHelper.trackStatusMappingCanceled(statusMappingAnalyticsPayload());
        setGroups(savedGroups);
        setNewColumnLabel("");
        setError(undefined);
    }, [savedGroups, statusMappingAnalyticsPayload]);

    const save = useCallback(async (): Promise<boolean> => {
        setSaving(true);
        setError(undefined);
        const analyticsPayload = statusMappingAnalyticsPayload();
        NovedadesExportAnalyticsHelper.trackStatusMappingSaveSubmitted(analyticsPayload);
        try {
            const payloadGroups = groups.map((group) => ({
                totalsField: group.totalsField,
                label: group.label,
                builtIn: group.builtIn,
                dayFlags: group.dayFlags,
                novedadAcronyms: group.novedadAcronyms,
            }));
            const result = await exportService.updateStatusMappingConfiguration(orgID, payloadGroups);
            const editableGroups = toEditableGroups(result.groups);
            setGroups(editableGroups);
            setSavedGroups(editableGroups);
            NovedadesExportAnalyticsHelper.trackStatusMappingSaveSucceeded(
                statusMappingAnalyticsPayload(editableGroups),
            );
            return true;
        } catch (saveError) {
            console.error(saveError);
            NovedadesExportAnalyticsHelper.trackStatusMappingSaveFailed(analyticsPayload);
            setError("No se pudo guardar la configuración de columnas.".localized());
            return false;
        } finally {
            setSaving(false);
        }
    }, [exportService, groups, orgID, statusMappingAnalyticsPayload]);

    return {
        groups,
        loading,
        saving,
        error,
        hasChanges,
        newColumnLabel,
        setNewColumnLabel,
        canCreateColumn,
        selectedNovedadesForGroup,
        resultsForGroup,
        setGroupQuery,
        setGroupDebouncedQuery,
        addNovedadToGroup,
        removeNovedadFromGroup,
        createColumn,
        removeGroup,
        cancel,
        save,
        load,
    };
};
